"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import { useLocale } from "next-intl";

// The two OneTrust controls on the cookie policy, driven by us rather than by
// OneTrust's own DOM scan.
//
// The documented snippet is `<button class="ot-sdk-show-settings">`, and on a
// plain page it works. It does not work here, and the reason is in their SDK
// (otBannerSdk.js 202601.2.0, `initCookieSettingHandlers`): the class is not
// bound with real event delegation. They add ONE listener to `document`, and
// the first click anywhere runs it, queries `.ot-sdk-show-settings` at that
// instant, attaches the real listeners to whatever exists, fires the handler if
// the click was on one of them, and then REMOVES itself from `document`.
//
// On this site the visitor reaches the cookie policy by clicking a footer link.
// That click IS the first click: the bootstrap runs while the cookie policy has
// not rendered yet, binds nothing, and takes itself off. The button then mounts
// with no listener at all. OneTrust's own SPA guide solves it exactly as we do
// here -- their sample assigns `onclick = () => OneTrust.ToggleInfoDisplay()`
// (developer.onetrust.com/onetrust/docs/single-page-applications).
//
// We also leave the class off the button deliberately. `insertCookieSettingText`
// overwrites the text of every `.ot-sdk-show-settings` element with the portal's
// CookieSettingButtonText, so wearing the class would let OneTrust relabel our
// button; and their rebind path (`initializeCookiePolicyHtml(true)`) uses
// querySelector, so it only ever rebinds the first one on the page.

// OneTrust arrives behind deferred GTM, so it is normal for it to be missing
// for a second or two after this mounts. Poll until it lands, then stop.
//
// The alternative is OneTrust's `OptanonWrapper` global, and we deliberately do
// not use it: their own docs put Google Consent Mode plumbing inside that
// function, so GTM's OneTrust template is a plausible owner of the same global,
// and a React effect assigning or wrapping it cannot see what the container
// already put there. Polling only READS `window.OneTrust` and owns nothing.
const POLL_MS = 400;
const MAX_WAIT_MS = 20_000;

function whenOneTrustReady(run: () => boolean): () => void {
  if (run()) return () => {};
  let waited = 0;
  const timer = setInterval(() => {
    waited += POLL_MS;
    if (run() || waited >= MAX_WAIT_MS) clearInterval(timer);
  }, POLL_MS);
  return () => clearInterval(timer);
}

// OneTrust picks its language when it initialises and never revisits it, so the
// language switcher would otherwise leave a Dutch cookie table on the English
// page. Both controls call this, and it is a no-op once the CMP already speaks
// the page's language -- which matters, because changeLanguage re-renders the
// preference centre and that re-opens it. Reading GetDomainData().Language
// rather than remembering what we last set keeps the two controls independent:
// whichever runs first does the work, the other sees it is already done.
function ensureLanguage(locale: string) {
  let culture: string | undefined;
  try {
    culture = window.OneTrust?.GetDomainData?.()?.Language?.Culture;
  } catch {
    // A CMP that cannot say which language it is in gets told anyway: not
    // knowing is a reason to set it, not a reason to skip it. One try around
    // both the read and the write skipped the write whenever the read threw,
    // which is the language race this function exists to close.
  }

  if (culture && culture.toLowerCase().split("-")[0] === locale.toLowerCase()) {
    return;
  }

  try {
    window.OneTrust?.changeLanguage?.(locale);
  } catch {
    // Wrong language beats no cookie policy.
  }
}

// A broken CMP release, or an extension that monkey-patches it, must not reach
// React's error boundary through a click handler or an effect body and take the
// page down with it.
function safely(run: () => void) {
  try {
    run();
  } catch {
    // Leaves the control inert, which is the state it was in before OneTrust
    // arrived at all.
  }
}

export function CookieSettingsButton({ label }: { label: string }) {
  const locale = useLocale();
  // The click that opens the panel can be the same click that loads OneTrust:
  // GTM starts on the first pointerdown, so someone landing straight on this
  // page hits a button whose SDK is still downloading. Waiting instead of
  // returning early is the difference between "opens a moment later" and "does
  // nothing until you click it twice".
  const cancel = useRef<(() => void) | null>(null);
  useEffect(() => () => cancel.current?.(), []);

  const openPreferences = () => {
    cancel.current?.();
    cancel.current = whenOneTrustReady(() => {
      const toggle = window.OneTrust?.ToggleInfoDisplay;
      if (!toggle) return false;
      // Before the toggle, not after: OneTrust can become ready between the
      // list's poll ticks, and a click landing in that window would otherwise
      // open the panel in whatever language the CMP defaulted to.
      ensureLanguage(locale);
      safely(toggle);
      return true;
    });
  };

  return (
    <button
      id="ot-sdk-btn"
      type="button"
      onClick={openPreferences}
      className="rounded-lg border border-line px-4 py-2 text-[15px] font-semibold text-navy transition-colors hover:bg-mist"
    >
      {label}
    </button>
  );
}

/**
 * OneTrust's generated cookie table, with our own list as the fallback.
 *
 * `children` is the hand-written inventory. It is rendered server-side and
 * stays visible until OneTrust actually puts something in the container, then
 * hides. That order matters: the table needs GTM, and the visitor most likely
 * to block GTM is the one reading the cookie policy. Waiting for real content
 * rather than for the call to return is the point -- a call that succeeds and
 * renders nothing would otherwise leave the page listing no cookies at all.
 */
export function CookieList({ children }: { children?: ReactNode }) {
  const locale = useLocale();
  const container = useRef<HTMLDivElement>(null);
  const [tableRendered, setTableRendered] = useState(false);

  useEffect(() => {
    const node = container.current;
    if (!node) return;

    // Tracks the container both ways, and asks for real content rather than
    // for any child at all. OneTrust renders a wrapper first and fills it with
    // groups after; a render that inserts the wrapper and then throws would
    // otherwise count as success and hide the whole inventory -- the exact
    // state this fallback exists to prevent. Latching would do the same on a
    // later render that empties the container without refilling it.
    //
    // If a future SDK stops emitting these class names the fallback stays up
    // alongside their table. That is the right way to be wrong: a duplicated
    // list is visible and fixable, a missing one is neither.
    const hasCookieTable = () =>
      node.querySelector(".ot-sdk-cookie-policy-group, table") !== null;
    const check = () => setTableRendered(hasCookieTable());
    const observer = new MutationObserver(check);
    observer.observe(node, { childList: true, subtree: true });
    check();

    const stopPolling = whenOneTrustReady(() => {
      const render = window.OneTrust?.initializeCookiePolicyHtml;
      if (!render) return false;
      ensureLanguage(locale);
      safely(render);
      return true;
    });

    return () => {
      observer.disconnect();
      stopPolling();
    };
  }, [locale]);

  return (
    <>
      <div id="ot-sdk-cookie-policy" ref={container} />
      {!tableRendered && children ? <div>{children}</div> : null}
    </>
  );
}
